import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import DailyManifests from './DailyManifests';
import DriverSettlements from './DriverSettlements';
import RateCardMatrix from './RateCardMatrix';
import ExceptionAudits from './ExceptionAudits';
import PayrollInvoicing from './PayrollInvoicing';
import GeneralLedger from './GeneralLedger';
import { createAccountingApi, isEndpointMissing } from './api';
import { DEFAULT_CUSTOMER, DEFAULT_RATE_CARDS, rateManifest } from './rateEngine';
import {
  DEFAULT_SETTINGS,
  buildInvoiceDraft,
  buildSettlements,
  currentInvoice,
  manifestDay,
  nextNumber,
  periodRange,
  periodStartFor,
} from './payroll';
import { DEFAULT_ACCOUNTS, buildJournal } from './ledger';
import { SAMPLE_DRIVERS, SAMPLE_DRIVER_CARDS, SAMPLE_MANIFESTS } from './mockData';

/** Screens shown as a dropdown under "Accounting" in the department sidebar. */
export const MANIFEST_SCREENS = [
  { key: 'daily-manifests', label: 'Daily Manifests', icon: 'fa-truck' },
  { key: 'driver-settlements', label: 'Driver Settlements', icon: 'fa-wallet' },
  { key: 'rate-card', label: 'Rate Card Matrix', icon: 'fa-table', adminOnly: true },
  { key: 'exception-audits', label: 'Exception Audits', icon: 'fa-tasks' },
  { key: 'payroll-invoicing', label: 'Payroll & Invoicing', icon: 'fa-file-invoice' },
  { key: 'general-ledger', label: 'General Ledger', icon: 'fa-book' },
];

/** Roles that may view and change rate cards (the backend enforces the same list). */
export const RATE_CARD_ROLES = ['company_admin', 'fleet_owner', 'platform_admin'];
export const canSeeScreen = (screen, role) => !screen.adminOnly || RATE_CARD_ROLES.includes(role);

const today = () => format(new Date(), 'yyyy-MM-dd');
const nowIso = () => new Date().toISOString();

const normalizeDriver = (d) => ({
  id: d.id,
  name: d.full_name || d.name || [d.first_name, d.last_name].filter(Boolean).join(' ') || d.email || d.id,
});

const upsertById = (list, item) => (list.some((x) => x.id === item.id) ? list.map((x) => (x.id === item.id ? item : x)) : [item, ...list]);
const mergeById = (list, incoming) => incoming.reduce(upsertById, list);

/** Supersede `invoice` with a fresh revision built from `manifests` (back-dated manifest posted into its period). */
const reviseInvoice = (invoice, manifests, cards, cfg) => {
  const draft = buildInvoiceDraft(manifests, cards, invoice.periodStart, invoice.customer, cfg);
  const revision = (invoice.revision || 0) + 1;
  return [
    { ...invoice, status: 'superseded', supersededAt: nowIso() },
    { ...draft, id: `${invoice.number}-R${revision}`, number: invoice.number, revision, status: 'issued', issuedAt: nowIso(), supersedes: invoice.id },
  ];
};

/**
 * Renders the selected Accounting sub-screen and owns the shared state (manifests, rate cards, settlements,
 * invoices, chart of accounts). Navigation lives in DepartmentPanel.
 *
 * Each call tries the backend first; when an endpoint isn't deployed yet (404/405/501 or unreachable)
 * the workspace switches to demo mode and applies the change locally.
 */
const ManifestsWorkspace = ({ BACKEND_URL, fetchWithAuth, screen, onNavigate }) => {
  const { user } = useAuth();
  const api = useMemo(() => createAccountingApi(BACKEND_URL, fetchWithAuth), [BACKEND_URL, fetchWithAuth]);

  const [demo, setDemo] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [cards, setCards] = useState(DEFAULT_RATE_CARDS);
  const [accounts, setAccounts] = useState(DEFAULT_ACCOUNTS);
  const [drivers, setDrivers] = useState([]);
  const [manifests, setManifests] = useState([]);
  const [settlementState, setSettlementState] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [day, setDayState] = useState(today);
  const [invoiceStart, setInvoiceStart] = useState(() => periodStartFor(today(), DEFAULT_SETTINGS.invoicePeriod));
  const [payStart, setPayStart] = useState(() => periodStartFor(today(), DEFAULT_SETTINGS.payPeriod));
  const [focusManifestId, setFocusManifestId] = useState(null);

  const demoRef = useRef(false);
  const manifestsRef = useRef(manifests);
  manifestsRef.current = manifests;

  const setDay = useCallback(
    (d) => {
      setDayState(d);
      setInvoiceStart(periodStartFor(d, settings.invoicePeriod));
      setPayStart(periodStartFor(d, settings.payPeriod));
    },
    [settings]
  );

  const enterDemo = useCallback(() => {
    if (demoRef.current) return;
    demoRef.current = true;
    setDemo(true);
    setDrivers((prev) => (prev.length ? prev : SAMPLE_DRIVERS));
    setCards((c) => (Object.keys(c.drivers || {}).length ? c : { ...c, drivers: SAMPLE_DRIVER_CARDS }));
    if (manifestsRef.current.length === 0) {
      setManifests(SAMPLE_MANIFESTS);
      setDay(SAMPLE_MANIFESTS.map(manifestDay).sort().pop());
    }
  }, [setDay]);

  /** Try the backend; fall back to `local()` when the endpoint doesn't exist yet. */
  const run = useCallback(
    async (remote, local) => {
      if (!demoRef.current) {
        try {
          return await remote();
        } catch (err) {
          if (!isEndpointMissing(err)) {
            toast.error(err.message);
            return null;
          }
          enterDemo();
        }
      }
      return local();
    },
    [enterDemo]
  );

  const isRateCardAdmin = RATE_CARD_ROLES.includes(user?.role);
  const [reloadKey, setReloadKey] = useState(0);

  // One-time reference data. Rate cards are admin-only; everyone else relies on server `manifest.pricing`.
  useEffect(() => {
    const warn = (what) => (err) => !isEndpointMissing(err) && toast.error(`${what}: ${err.message}`);
    api.getSettings().then((s) => s && setSettings((d) => ({ ...d, ...s }))).catch(warn('Settings'));
    if (isRateCardAdmin) api.getRateCards().then((c) => c && setCards({ ...DEFAULT_RATE_CARDS, ...c })).catch(warn('Rate cards'));
    api.listAccounts().then((a) => a?.length && setAccounts(a)).catch(warn('Chart of accounts'));
    api.listDrivers().then((list) => setDrivers(list.map(normalizeDriver))).catch(() => setDrivers(SAMPLE_DRIVERS));
  }, [api, isRateCardAdmin]);

  // Period-scoped data: manifests covering both the invoice week and the pay period on screen.
  useEffect(() => {
    if (demo) return;
    const inv = periodRange(invoiceStart, settings.invoicePeriod);
    const pay = periodRange(payStart, settings.payPeriod);
    api
      .listManifests(inv.from < pay.from ? inv.from : pay.from, inv.to > pay.to ? inv.to : pay.to)
      .then((list) => setManifests((prev) => mergeById(prev, list)))
      .catch((err) => (isEndpointMissing(err) ? enterDemo() : toast.error(`Manifests: ${err.message}`)));
    api
      .listSettlements(payStart)
      .then((list) => setSettlementState((prev) => ({ ...prev, ...Object.fromEntries(list.map((s) => [s.id, s])) })))
      .catch(() => {});
    api
      .listInvoices(invoiceStart)
      .then((list) => setInvoices((prev) => mergeById(prev, list)))
      .catch(() => {});
  }, [api, invoiceStart, payStart, settings, demo, enterDemo, reloadKey]);

  // ---------- Manifests ----------
  const replaceManifest = (m) => setManifests((prev) => upsertById(prev, m));

  const handleUpload = (file) =>
    run(
      async () => {
        const created = await api.uploadManifest(file);
        replaceManifest(created);
        setDay(manifestDay(created));
        toast.success(`Manifest ${created.route} parsed`);
        return created;
      },
      () => {
        toast.warning(`Parser endpoint not available yet — showing sample data (${file.name})`);
        return null;
      }
    );

  const assignDriver = (manifest, driver) =>
    run(
      async () => replaceManifest(await api.updateManifest(manifest.id, { driver })),
      () => replaceManifest({ ...manifest, driver })
    );

  /** decision: 'approved' | 'waived' | null (reopen). */
  const reviewPieces = (manifest, barcodes, decision, note = '') => {
    const review = decision ? { decision, note, by: user?.full_name || user?.email || 'You', at: nowIso() } : null;
    return run(
      async () => {
        let updated = manifest;
        for (const barcode of barcodes) updated = await api.reviewPiece(manifest.id, barcode, decision ? { decision, note } : { decision: null });
        replaceManifest(updated);
      },
      () =>
        replaceManifest({
          ...manifest,
          stops: manifest.stops.map((s) => ({
            ...s,
            pieces: s.pieces.map((p) => (barcodes.includes(p.barcode) ? { ...p, review } : p)),
          })),
        })
    );
  };

  /** Posting a back-dated manifest into an already-invoiced week re-issues that week's invoice. */
  const postManifest = (manifest) => {
    const invStart = periodStartFor(manifestDay(manifest), settings.invoicePeriod);
    const customer = manifest.customer || DEFAULT_CUSTOMER;
    return run(
      async () => {
        replaceManifest(await api.postManifest(manifest.id));
        const refreshed = await api.listInvoices(invStart).catch(() => []);
        setInvoices((prev) => mergeById(prev, refreshed));
        toast.success(`${manifest.route} posted to the General Ledger`);
      },
      () => {
        const posted = { ...manifest, status: 'posted', postedAt: nowIso(), pricing: { pieces: rateManifest(manifest, cards).pieces } };
        const nextManifests = upsertById(manifests, posted);
        setManifests(nextManifests);
        const inv = currentInvoice(invoices, invStart, customer);
        if (inv) {
          const [old, revised] = reviseInvoice(inv, nextManifests, cards, settings.invoicePeriod);
          setInvoices((prev) => mergeById(prev, [old, revised]));
          toast.success(`${manifest.route} posted · back-dated, so Final Invoice ${inv.number} was issued`);
        } else {
          toast.success(`${manifest.route} posted (local demo)`);
        }
      }
    );
  };

  // ---------- Rate cards ----------
  const saveCustomerCard = (card) =>
    run(
      async () => {
        const saved = await api.saveCustomerCard(card);
        setCards((c) => ({ ...c, customers: { ...c.customers, [saved.customer]: saved } }));
        setReloadKey((k) => k + 1); // re-fetch server pricing of unposted manifests
        toast.success(`${card.customer} rate card saved`);
      },
      () => {
        setCards((c) => ({ ...c, customers: { ...c.customers, [card.customer]: card } }));
        toast.success(`${card.customer} rate card updated (local demo)`);
      }
    );

  const saveDriverCard = (card) =>
    run(
      async () => {
        const saved = await api.saveDriverCard(card);
        setCards((c) => ({ ...c, drivers: { ...c.drivers, [saved.driverId]: saved } }));
        setReloadKey((k) => k + 1);
        toast.success(`Rate card saved for ${card.driverName}`);
      },
      () => {
        setCards((c) => ({ ...c, drivers: { ...c.drivers, [card.driverId]: card } }));
        toast.success(`Rate card updated for ${card.driverName} (local demo)`);
      }
    );

  const removeDriverCard = (driverId) => {
    const drop = () =>
      setCards((c) => {
        const { [driverId]: _, ...rest } = c.drivers;
        return { ...c, drivers: rest };
      });
    return run(
      async () => {
        await api.deleteDriverCard(driverId);
        drop();
        setReloadKey((k) => k + 1);
      },
      drop
    );
  };

  const saveTaxes = (taxes) =>
    run(
      async () => {
        const saved = await api.saveTaxes(taxes);
        setCards((c) => ({ ...c, taxes: saved }));
        toast.success('Taxes saved');
      },
      () => {
        setCards((c) => ({ ...c, taxes }));
        toast.success('Taxes updated (local demo)');
      }
    );

  // ---------- Settlements ----------
  const putSettlement = (rec) => setSettlementState((prev) => ({ ...prev, [rec.id]: { ...prev[rec.id], ...rec } }));
  const base = (s) => ({ id: s.id, periodStart: s.periodStart, driver: s.driver, adjustments: s.adjustments });

  const saveAdjustments = (s, adjustments) =>
    run(
      async () => putSettlement(await api.saveSettlementAdjustments(s.id, adjustments)),
      () => putSettlement({ ...base(s), status: s.status, adjustments })
    );

  const approveSettlement = (s) =>
    run(
      async () => putSettlement(await api.approveSettlement(s.id)),
      () => putSettlement({ ...base(s), status: 'approved', approvedAt: nowIso(), manifestIds: s.lines.map((l) => l.manifestId) })
    );

  /** The accountant has finalized payroll for this gross pay sheet line. */
  const markProcessed = (s) =>
    run(
      async () => putSettlement(await api.markSettlementProcessed(s.id)),
      () => putSettlement({ ...base(s), status: 'processed', processedAt: nowIso() })
    );

  const settlements = useMemo(
    () => buildSettlements(manifests, cards, payStart, settlementState, settings.payPeriod),
    [manifests, cards, payStart, settlementState, settings]
  );

  // Every persisted settlement (any period) for the ledger.
  const allSettlements = useMemo(() => {
    const periods = [...new Set(Object.values(settlementState).map((s) => s.periodStart))];
    return periods.flatMap((p) => buildSettlements(manifests, cards, p, settlementState, settings.payPeriod)).filter((s) => settlementState[s.id]);
  }, [manifests, cards, settlementState, settings]);

  // ---------- Invoices ----------
  const createInvoice = (customer) =>
    run(
      async () => {
        const inv = await api.createInvoice(invoiceStart, customer);
        setInvoices((prev) => upsertById(prev, inv));
        toast.success(`Invoice ${inv.number} issued`);
      },
      () => {
        const draft = buildInvoiceDraft(manifests, cards, invoiceStart, customer, settings.invoicePeriod);
        const number = nextNumber('INV', invoices.map((i) => i.number));
        setInvoices((prev) => upsertById(prev, { ...draft, id: number, number, revision: 0, status: 'issued', issuedAt: nowIso() }));
        toast.success(`Invoice ${number} issued (local demo)`);
      }
    );

  const reissueInvoice = (invoice) =>
    run(
      async () => {
        const revised = await api.reissueInvoice(invoice.id);
        setInvoices((prev) => mergeById(prev, [{ ...invoice, status: 'superseded' }, revised]));
        toast.success(`Final Invoice ${revised.number} issued`);
      },
      () => {
        const pair = reviseInvoice(invoice, manifests, cards, settings.invoicePeriod);
        setInvoices((prev) => mergeById(prev, pair));
        toast.success(`Final Invoice ${invoice.number} issued (local demo)`);
      }
    );

  // ---------- Ledger ----------
  const saveAccounts = (next) =>
    run(
      async () => {
        setAccounts(await api.saveAccounts(next));
        toast.success('Chart of accounts saved');
      },
      () => {
        setAccounts(next);
        toast.success('Chart of accounts updated (local demo)');
      }
    );

  const localJournal = useMemo(
    () => buildJournal({ manifests, cards, settlements: allSettlements, invoices }),
    [manifests, cards, allSettlements, invoices]
  );

  const loadJournal = useCallback(
    async (from, to) => {
      if (demoRef.current) return null;
      try {
        return await api.listJournal(from, to);
      } catch (err) {
        if (!isEndpointMissing(err)) toast.error(`General Ledger: ${err.message}`);
        return null;
      }
    },
    [api]
  );

  const openManifest = (id) => {
    const m = manifests.find((x) => x.id === id);
    if (m) setDay(manifestDay(m));
    setFocusManifestId(id);
    onNavigate('daily-manifests');
  };

  const current = MANIFEST_SCREENS.find((s) => s.key === screen);
  const invoicePeriod = { start: invoiceStart, cfg: settings.invoicePeriod, onChange: setInvoiceStart };
  const payPeriod = { start: payStart, cfg: settings.payPeriod, onChange: setPayStart };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <span>Accounting</span>
          <i className="fas fa-chevron-right text-[10px]"></i>
          <span className="text-foreground font-semibold">{current?.label}</span>
        </div>
        {demo && (
          <span id="manifests-demo-banner" className="text-xs px-2 py-1 rounded border border-amber-500/40 text-amber-500 bg-amber-500/10">
            <i className="fas fa-flask mr-1"></i>Demo data — accounting endpoints not live yet, changes stay in this browser tab
          </span>
        )}
      </div>

      {screen === 'daily-manifests' && (
        <DailyManifests
          manifests={manifests}
          cards={cards}
          drivers={drivers}
          day={day}
          onDayChange={setDay}
          focusId={focusManifestId}
          onUpload={handleUpload}
          onAssignDriver={assignDriver}
          onPost={postManifest}
          onOpenSettlement={() => onNavigate('driver-settlements')}
          onOpenExceptions={() => onNavigate('exception-audits')}
          onOpenRateCard={() => onNavigate('rate-card')}
        />
      )}
      {screen === 'driver-settlements' && (
        <DriverSettlements
          period={payPeriod}
          settlements={settlements}
          onSaveAdjustments={saveAdjustments}
          onApprove={approveSettlement}
          onMarkProcessed={markProcessed}
          onOpenManifest={openManifest}
        />
      )}
      {screen === 'rate-card' && !canSeeScreen(current, user?.role) && (
        <div className="p-12 text-center text-muted-foreground border border-dashed border-border rounded-lg">
          <i className="fas fa-lock text-3xl mb-3"></i>
          <p>Only administrators can view or change rate cards.</p>
        </div>
      )}
      {screen === 'rate-card' && canSeeScreen(current, user?.role) && (
        <RateCardMatrix
          cards={cards}
          manifests={manifests}
          drivers={drivers}
          onSaveCustomerCard={saveCustomerCard}
          onSaveDriverCard={saveDriverCard}
          onDeleteDriverCard={removeDriverCard}
          onSaveTaxes={saveTaxes}
        />
      )}
      {screen === 'exception-audits' && (
        <ExceptionAudits
          period={invoicePeriod}
          manifests={manifests}
          cards={cards}
          onReview={reviewPieces}
          onOpenManifest={openManifest}
          onOpenRateCard={() => onNavigate('rate-card')}
        />
      )}
      {screen === 'payroll-invoicing' && (
        <PayrollInvoicing
          payPeriod={payPeriod}
          invoicePeriod={invoicePeriod}
          manifests={manifests}
          cards={cards}
          settlements={settlements}
          invoices={invoices}
          onCreateInvoice={createInvoice}
          onReissueInvoice={reissueInvoice}
        />
      )}
      {screen === 'general-ledger' && (
        <GeneralLedger localEntries={localJournal} loadJournal={loadJournal} demo={demo} accounts={accounts} onSaveAccounts={saveAccounts} />
      )}
    </div>
  );
};

export default ManifestsWorkspace;
